/**
 * The request headers a file write carries: its version check (`If-Match`, or `If-None-Match: *`
 * for a create), and the label and metadata of the version it writes (`X-Data-Label`,
 * `X-Data-Metadata`). Built here for both `wayai files put` and `wayai files push`, so the two
 * cannot spell them differently.
 *
 * A header value travels as bytes the far side reads as text, so these refuse or escape anything
 * past printable ASCII rather than let the runtime reject it — or a hop decode it differently.
 */

import { expected } from '../../lib/expected.js';

/**
 * A content hash as `If-Match` wants it: quoted, the way a download's `ETag` arrives. Takes the
 * bare hash, or the `ETag` pasted as-is (quoted, or weak with `W/`).
 */
export function ifMatchValue(raw: string): string {
  const hash = raw.trim().replace(/^W\//, '').replace(/^"(.*)"$/, '$1');
  if (!/^[\x21\x23-\x7e]+$/.test(hash)) {
    throw expected(
      `--if-match must be the file's content hash (its ETag, or content_sha256 from \`wayai files get --meta\`), got ${JSON.stringify(raw)}.`,
    );
  }
  return `"${hash}"`;
}

/** A version label. Printable ASCII only: a header cannot carry other characters intact. */
export function labelValue(raw: string): string {
  if (raw.length === 0 || !/^[\x20-\x7e]+$/.test(raw)) {
    throw expected(
      `--label must be printable ASCII text (a request header cannot carry other characters intact), got ${JSON.stringify(raw)}.`,
    );
  }
  return raw;
}

/**
 * Metadata as the header carries it: a JSON object with every character past ASCII written as a
 * `\uXXXX` escape, which parses back to the same value on the far side.
 */
export function metadataValue(value: unknown): string {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw expected('--metadata must be a JSON object.');
  }
  return JSON.stringify(value).replace(
    /[\u007f-￿]/g,
    (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`,
  );
}

/**
 * The headers for one file write; a field left undefined sends no header. `createOnly` sends
 * `If-None-Match: *`: the base refuses the upload with a 412 if a file already holds the path, and
 * refuses it outright beside `ifMatch`.
 */
export function fileWriteHeaders(opts: {
  ifMatch?: string;
  createOnly?: boolean;
  label?: string;
  metadata?: unknown;
}): Record<string, string> {
  const headers: Record<string, string> = {};
  if (opts.ifMatch !== undefined) headers['If-Match'] = ifMatchValue(opts.ifMatch);
  if (opts.createOnly) headers['If-None-Match'] = '*';
  if (opts.label !== undefined) headers['X-Data-Label'] = labelValue(opts.label);
  if (opts.metadata !== undefined) headers['X-Data-Metadata'] = metadataValue(opts.metadata);
  return headers;
}
